import type { OpportunityRelatedExtraGuestSurcharge } from '~/hooks/apis/offers/useGetOpportunityRelated';

import type { EgsGuestSlot, EgsRoomGroup } from './types';

const GUEST_TYPE_TO_SLOT: Record<string, EgsGuestSlot> = {
  'extra adult': 'adult',
  'extra child': 'child',
  'extra infant': 'infant',
  'extra teenager': 'teenager',
};

const ARCHIVED_NAME_PREFIX = /^archived\s+/i;

const GUEST_SLOT_ORDER: Array<EgsGuestSlot> = ['adult', 'teenager', 'child', 'infant'];

export function getGuestSlot(extraGuestType: string | null | undefined): EgsGuestSlot | null {
  return GUEST_TYPE_TO_SLOT[(extraGuestType ?? '').trim().toLowerCase()] ?? null;
}

/**
 * There is no room lookup on the Salesforce object. CMs put the room in the record
 * name instead, and supersede a row by prefixing the name with ARCHIVED, so the room
 * a surcharge belongs to is the name with that prefix removed.
 */
export function getRoomLabel(name: string | null | undefined) {
  return (name ?? '').replace(ARCHIVED_NAME_PREFIX, '').trim();
}

export function isArchivedByName(name: string | null | undefined) {
  return ARCHIVED_NAME_PREFIX.test((name ?? '').trim());
}

export function groupEgsByRoom(surcharges: Array<OpportunityRelatedExtraGuestSurcharge>): Array<EgsRoomGroup> {
  const byRoom = new Map<string, Array<OpportunityRelatedExtraGuestSurcharge>>();

  for (const surcharge of surcharges) {
    const roomLabel = getRoomLabel(surcharge.name);
    byRoom.set(roomLabel, [...(byRoom.get(roomLabel) ?? []), surcharge]);
  }

  return [...byRoom.entries()]
    .map(([roomLabel, rows]) => ({
      conflictingSlots: getConflictingSlots(rows),
      roomLabel,
      rows,
      slots: getRoomSlots(rows),
    }))
    .sort((a, b) => a.roomLabel.localeCompare(b.roomLabel));
}

/**
 * Superseded rows stay in the detail table, but a slot whose only row is archived
 * is not live, so it must not show up in the room's guest type summary.
 */
function getRoomSlots(rows: Array<OpportunityRelatedExtraGuestSurcharge>): Array<EgsGuestSlot> {
  const slots = rows
    .filter((row) => !isArchivedByName(row.name))
    .map((row) => getGuestSlot(row.extra_guest_type__c))
    .filter((slot): slot is EgsGuestSlot => !!slot);

  return sortBySlotOrder([...new Set(slots)]);
}

function sortBySlotOrder(slots: Array<EgsGuestSlot>): Array<EgsGuestSlot> {
  return [...slots].sort((a, b) => GUEST_SLOT_ORDER.indexOf(a) - GUEST_SLOT_ORDER.indexOf(b));
}

export function formatSlotList(slots: Array<EgsGuestSlot>) {
  if (slots.length <= 1) {
    return slots[0] ?? '';
  }

  return `${slots.slice(0, -1).join(', ')} and ${slots[slots.length - 1]}`;
}

/**
 * Archived rows are how a CM supersedes a surcharge, so an archived row sitting
 * beside its live replacement is the expected state rather than a conflict. Only
 * live rows can genuinely compete for the same slot.
 */
function getConflictingSlots(rows: Array<OpportunityRelatedExtraGuestSurcharge>) {
  const counts = new Map<EgsGuestSlot, number>();

  for (const row of rows) {
    if (isArchivedByName(row.name)) {
      continue;
    }

    const slot = getGuestSlot(row.extra_guest_type__c);
    if (slot) {
      counts.set(slot, (counts.get(slot) ?? 0) + 1);
    }
  }

  return sortBySlotOrder([...counts.entries()].filter(([, count]) => count > 1).map(([slot]) => slot));
}

export function countRoomRatesWithEgs(extraGuestSurchargesByRoomRate: Record<string, Array<unknown>>) {
  return Object.values(extraGuestSurchargesByRoomRate).filter((records) => records.length > 0).length;
}
